/**
 * Application router — React Router v7, lazy route modules.
 *
 * Route groups mirror the legacy Next.js app-router structure; pages are
 * ported wave by wave (see MIGRATION_PARITY.md). Unported routes render a
 * placeholder pointing at the legacy app until parity.
 */

import { createBrowserRouter, Navigate, redirect } from 'react-router';

import { DashboardLayout } from '@/app/layouts/DashboardLayout';
import { RouteError } from '@/app/AppError';
import { AuthCallback, SilentRenew } from '@/routes/auth/callbacks';
import { ProtectedRoute } from '@/auth/ProtectedRoute';
import { AdminPortalLayout } from '@/routes/admin/AdminPortalLayout';
import { loadConfig } from '@/config';
import { ADMIN_PORTAL_HOME, adminPortalUrlFor, appUrlFor, isAdminHost } from '@/lib/adminPortal';
import { NotFoundPage } from '@/routes/NotFoundPage';
import { VersionsBuiltPage } from '@/routes/versions/VersionsBuiltPage';
import { useT } from '@/i18n';

/**
 * Full-screen splash rendered during the router's initial hydration, while the
 * first matched lazy route module resolves. Wired as the root route's
 * `HydrateFallback` so React Router has an element to render on initial load —
 * this silences the app-wide "No `HydrateFallback` element provided to render
 * during initial hydration" warning on every page. Token-themed (light + dark).
 */
function SplashScreen() {
  const { t } = useT();
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex h-screen w-full flex-col items-center justify-center gap-4 bg-bg text-foreground"
    >
      <span className="h-10 w-10 animate-spin rounded-full border-2 border-border border-t-primary" />
      <span className="text-sm text-muted">{t('shell.app.router.loading')}</span>
      <span className="sr-only">{t('shell.app.router.loadingTheApplication')}</span>
    </div>
  );
}

export const router = createBrowserRouter([
  { path: '/auth/callback', element: <AuthCallback /> },
  { path: '/auth/silent-renew', element: <SilentRenew /> },
  // Public build-provenance page — no auth, so version parity can be checked
  // even when login is unavailable. See VersionsBuiltPage.
  { path: '/versionsbuilt', element: <VersionsBuiltPage /> },
  // Org-invite acceptance (collaboration, docs/COLLABORATION_CONTRACT.md §1) —
  // deliberately NOT under ProtectedRoute: an invitee may be account-less, and
  // the backend provisions their Keycloak account on accept. See
  // routes/team/AcceptOrgInvitePage.tsx for the full rationale.
  // PUBLIC "Sign in with your company SSO": work email -> tenant IdP alias
  // (public discovery) -> sign-in with kc_idp_hint. Deliberately NOT under
  // ProtectedRoute: the visitor is not signed in yet.
  {
    path: '/sso',
    lazy: async () => {
      const { CompanySsoPage } = await import('@/routes/auth/CompanySsoPage');
      return { Component: CompanySsoPage };
    },
  },
  {
    path: '/accept-org-invite/:token',
    lazy: async () => {
      const { AcceptOrgInvitePage } = await import('@/routes/team/AcceptOrgInvitePage');
      return { Component: AcceptOrgInvitePage };
    },
  },
  // Content-community group-invite acceptance — same rationale as the org-invite
  // route above: an invited member (especially an EXTERNAL user) may be account-
  // less, so this is deliberately NOT under ProtectedRoute. See
  // routes/communities/AcceptCommunityInvitePage.tsx.
  {
    path: '/accept-community-invite/:token',
    lazy: async () => {
      const { AcceptCommunityInvitePage } = await import(
        '@/routes/communities/AcceptCommunityInvitePage'
      );
      return { Component: AcceptCommunityInvitePage };
    },
  },
  // PUBLIC booking pages (Calendly-style) — an invitee has no account, so these
  // are deliberately NOT under ProtectedRoute. They call the public, token/slug-
  // authenticated crm-service endpoints (/api/v1/crm/book/:slug, /booking/:token).
  {
    path: '/book/:slug',
    lazy: async () => {
      const { PublicBookingPage } = await import('@/routes/booking/PublicBookingPage');
      return { Component: PublicBookingPage };
    },
  },
  {
    path: '/booking/:token',
    lazy: async () => {
      const { ManageBookingPage } = await import('@/routes/booking/ManageBookingPage');
      return { Component: ManageBookingPage };
    },
  },
  // PUBLIC web-form capture page — the `/f/{token}` embed link a CRM form hands
  // out. Deliberately NOT under ProtectedRoute: visitors are account-less and
  // the crm-service `/forms/{token}` endpoints are token-authenticated (public).
  {
    path: '/f/:token',
    lazy: async () => {
      const { PublicFormPage } = await import('@/routes/forms/PublicFormPage');
      return { Component: PublicFormPage };
    },
  },
  // PUBLIC e-signature page — the `/sign/{token}` link a sent CRM quote/proposal
  // hands out (crm.ts documentPublicUrl). Deliberately NOT under ProtectedRoute:
  // the signer is account-less; the crm-service `/sign/{token}` endpoints resolve
  // the tenant from the sign-token row (no JWT). See routes/sign/PublicSignPage.
  {
    path: '/sign/:token',
    lazy: async () => {
      const { PublicSignPage } = await import('@/routes/sign/PublicSignPage');
      return { Component: PublicSignPage };
    },
  },
  // PUBLIC link-in-bio page — the `/p/{slug}` link the bio builder hands out
  // (bio.ts publicUrl). Deliberately NOT under ProtectedRoute: visitors are
  // account-less; bio-service `/public/bio/{slug}` resolves the tenant from the
  // slug row (no JWT) and forwards captured leads via the bio's capture token.
  {
    path: '/p/:slug',
    lazy: async () => {
      const { PublicBioPage } = await import('@/routes/bio/PublicBioPage');
      return { Component: PublicBioPage };
    },
  },
  // I5 / R14 — PUBLIC waitlist double opt-in confirmation (the link in the
  // confirmation email). Records express consent, then shows queue position +
  // the referral share link. No JWT.
  {
    path: '/p/:slug/waitlist/confirm',
    lazy: async () => {
      const { WaitlistConfirmPage } = await import('@/routes/bio/WaitlistConfirmPage');
      return { Component: WaitlistConfirmPage };
    },
  },
  // R15 PUBLIC partner portal — the magic link a tenant shares with a partner of
  // its referral / affiliate program. Deliberately NOT under ProtectedRoute: the
  // partner has no Karexia account; ai-orchestrator's public portal endpoints
  // authenticate the single-use magic token / portal session themselves.
  {
    path: '/partner/:token',
    lazy: async () => {
      const { PartnerPortalPage } = await import('@/routes/growth/PartnerPortalPage');
      return { Component: PartnerPortalPage };
    },
  },
  // PUBLIC guest content-approval page — the `/public/approvals/{token}` link a
  // minted guest invite hands out (content-approvals.ts guestApprovalUrl).
  // Deliberately NOT under ProtectedRoute: the guest reviewer is account-less;
  // content-service `/public/approvals/{token}` resolves the invite from the
  // token row (no JWT) and records the approve/reject/comment decision.
  {
    path: '/public/approvals/:token',
    lazy: async () => {
      const { PublicApprovalPage } = await import('@/routes/approvals/PublicApprovalPage');
      return { Component: PublicApprovalPage };
    },
  },
  // PUBLIC client portal (Epic 5d) — the `/public/approval/{token}` branded,
  // no-login approval portal a client reviewer reaches from a shared link. A
  // multi-item, read-only calendar/list with approve / request-changes /
  // comment per item. Deliberately NOT under ProtectedRoute: the reviewer is
  // account-less; content-service resolves the invite from the token (no JWT),
  // and the page itself never sends an Authorization header (see lib/api/portal)
  // and sets `noindex` at runtime. Distinct from /public/approvals (plural),
  // which is the legacy single-item guest surface.
  {
    path: '/public/approval/:token',
    lazy: async () => {
      const { ClientPortalPage } = await import('@/routes/portal/ClientPortalPage');
      return { Component: ClientPortalPage };
    },
  },
  // PUBLIC CSAT survey response page — the `/csat/{token}` link a sent survey
  // hands out. Account-less; community-service `/webhooks/csat/{token}` resolves
  // the survey from the token row (no JWT) and records the score/comment.
  {
    path: '/csat/:token',
    lazy: async () => {
      const { PublicCsatPage } = await import('@/routes/community/PublicCsatPage');
      return { Component: PublicCsatPage };
    },
  },
  // PUBLIC platform status page (E9) — deliberately NOT under ProtectedRoute: it
  // polls the account-less public GET /api/v1/status (sanitized component states
  // + SLO targets, no auth). Uptime monitors and logged-out users can reach it.
  {
    path: '/status',
    lazy: async () => {
      const { StatusPage } = await import('@/routes/status/StatusPage');
      return { Component: StatusPage };
    },
  },
  // PUBLIC privacy / confidentialité policy — deliberately NOT under
  // ProtectedRoute so a logged-out visitor (and links from the signup page, the
  // Keycloak login page, the app footer and the email CASL footer) can reach it.
  // Both slugs render the same self-contained FR/EN/AR page. Law-25/GDPR/CASL.
  {
    path: '/confidentialite',
    lazy: async () => {
      const { PrivacyPolicyPage } = await import('@/routes/legal/PrivacyPolicyPage');
      return { Component: PrivacyPolicyPage };
    },
  },
  {
    path: '/privacy',
    lazy: async () => {
      const { PrivacyPolicyPage } = await import('@/routes/legal/PrivacyPolicyPage');
      return { Component: PrivacyPolicyPage };
    },
  },
  // PUBLIC Meta (Facebook/Instagram) Data Deletion instructions + status page —
  // required by Meta App Review's "Data Deletion Instructions URL", and the
  // confirmation URL the Data Deletion Callback returns
  // (community-service POST /webhooks/meta/data-deletion ->
  // https://www.karexia.com/data-deletion?code=<code>). Deliberately NOT under
  // ProtectedRoute: a visitor arriving from Meta is account-less. Both slugs
  // render the same self-contained FR/EN/AR page; `?code=` shows request status.
  {
    path: '/data-deletion',
    lazy: async () => {
      const { DataDeletionPage } = await import('@/routes/legal/DataDeletionPage');
      return { Component: DataDeletionPage };
    },
  },
  {
    path: '/suppression-donnees',
    lazy: async () => {
      const { DataDeletionPage } = await import('@/routes/legal/DataDeletionPage');
      return { Component: DataDeletionPage };
    },
  },
  // PUBLIC proof page (R18) — customer case studies published with stored
  // written consent (compliance-service public list). NOT under ProtectedRoute.
  {
    path: '/results',
    lazy: async () => ({ Component: (await import('@/routes/results/ResultsPage')).ResultsPage }),
  },
  {
    path: '/resultats',
    lazy: async () => ({ Component: (await import('@/routes/results/ResultsPage')).ResultsPage }),
  },
  {
    // PUBLIC Terms of Use (CGU) — same public placement as /confidentialite.
    path: '/conditions',
    lazy: async () => {
      const { TermsPage } = await import('@/routes/legal/TermsPage');
      return { Component: TermsPage };
    },
  },
  {
    path: '/terms',
    lazy: async () => {
      const { TermsPage } = await import('@/routes/legal/TermsPage');
      return { Component: TermsPage };
    },
  },
  {
    // Global-admin portal, served only at admin.karexia.com in prod: on the app
    // host, /admin/* redirects there (config.adminPortalUrl). Its own shell
    // (AdminPortalLayout), NOT DashboardLayout; AdminPortalLayout gates on the
    // platform_superadmin realm role. Section pages are lazy-loaded.
    path: '/admin',
    // The router is created at import time, before main.tsx loads config.json, so
    // the first navigation's loaders must load (memoized) config themselves.
    loader: async ({ request }) => {
      const url = new URL(request.url);
      const target = adminPortalUrlFor(url.pathname + url.search, await loadConfig());
      return target ? redirect(target) : null;
    },
    HydrateFallback: SplashScreen,
    element: (
      <ProtectedRoute>
        <AdminPortalLayout />
      </ProtectedRoute>
    ),
    errorElement: <RouteError />,
    children: [
      { index: true, lazy: async () => ({ Component: (await import('@/routes/admin/AdminPortalHome')).AdminPortalHome }) },
      { path: 'fleet', lazy: async () => ({ Component: (await import('@/routes/admin/FleetAdminPage')).FleetAdminPage }) },
      { path: 'credits', lazy: async () => ({ Component: (await import('@/routes/admin/AdminCreditsPage')).AdminCreditsPage }) },
      { path: 'models', lazy: async () => ({ Component: (await import('@/routes/admin/ModelsCatalogPage')).ModelsCatalogPage }) },
      { path: 'plans', lazy: async () => ({ Component: (await import('@/routes/admin/PlansAdminPage')).PlansAdminPage }) },
      // S7 — voucher mint/list/deactivate (platform superadmin; moved off /settings/referral).
      { path: 'vouchers', lazy: async () => ({ Component: (await import('@/routes/admin/VouchersAdminPage')).VouchersAdminPage }) },
      { path: 'entitlements', lazy: async () => ({ Component: (await import('@/routes/admin/EntitlementsAdminPage')).EntitlementsAdminPage }) },
      { path: 'integrations', lazy: async () => ({ Component: (await import('@/routes/admin/IntegrationsStatusPage')).IntegrationsStatusPage }) },
      { path: 'settings', lazy: async () => ({ Component: (await import('@/routes/admin/PlatformSettingsPage')).PlatformSettingsPage }) },
      // Content Communities — org-wide engagement defaults + the global feature
      // activation flag. Admin portal ONLY (platform_superadmin); the member-facing
      // /communities hub is unchanged and keeps its conservative defaults.
      { path: 'communities', lazy: async () => ({ Component: (await import('@/routes/admin/communities/CommunitiesDefaultsPage')).CommunitiesDefaultsPage }) },
      // Privacy & Compliance — DSAR intake/processing/export, retention policies,
      // unified audit (compliance-service /api/compliance privacy-ops). Admin
      // portal ONLY (platform_superadmin); every call is tenant-scoped server-side.
      { path: 'privacy', lazy: async () => ({ Component: (await import('@/routes/admin/privacy/PrivacyCompliancePage')).PrivacyCompliancePage }) },
      // R18 — review queue for customer case studies (consent-gated publication).
      { path: 'case-studies', lazy: async () => ({ Component: (await import('@/routes/admin/AdminCaseStudiesPage')).AdminCaseStudiesPage }) },
      // I8 — in-app human support inbox (all tenants; platform_superadmin).
      { path: 'support', lazy: async () => ({ Component: (await import('@/routes/admin/SupportInboxPage')).SupportInboxPage }) },
    ],
  },
  {
    path: '/',
    // Root route carries the app-wide hydration fallback: every in-app page
    // matches under it, so React Router always finds a splash to render while
    // lazy route modules load — no more "No HydrateFallback element" warning.
    HydrateFallback: SplashScreen,
    // The admin host serves only the console: tenant pages go back to the app
    // host (config.appUrl). `/` itself is left to the index loader below.
    loader: async ({ request }) => {
      const url = new URL(request.url);
      const target = appUrlFor(url.pathname + url.search, await loadConfig());
      return target ? redirect(target) : null;
    },
    element: (
      <ProtectedRoute>
        <DashboardLayout />
      </ProtectedRoute>
    ),
    // Friendly full-page fallback for loader/lazy/render errors in the app tree.
    errorElement: <RouteError />,
    children: [
      {
        index: true,
        // On the dedicated admin subdomain (admin.karexia.com) the HOME is the
        // global-admin console, not the tenant dashboard. Redirect in the index
        // loader — before the client shell renders — so admin.* lands straight on
        // /admin (and never hits the tenant DashboardPage, which has no data on
        // the admin host and was erroring there). The /admin tree's own
        // platform_superadmin guard remains the authoritative access control.
        loader: () => (isAdminHost() ? redirect(ADMIN_PORTAL_HOME) : null),
        lazy: async () => {
          const { DashboardPage } = await import('@/routes/dashboard/DashboardPage');
          return { Component: DashboardPage };
        },
      },
      // Team HQ — "Centre de commande — Équipe 24/7": mission control for the
      // 24/7 auto-coordinated virtual team (master always-on switch, mandate,
      // coordinator cadence/budget, per-persona autonomy, live status).
      {
        path: 'ai-team/hq',
        lazy: async () => {
          const { TeamHQPage } = await import('@/routes/ai-team/TeamHQPage');
          return { Component: TeamHQPage };
        },
      },
      {
        path: 'ai-team/office',
        lazy: async () => {
          const { OfficePage } = await import('@/routes/ai-team/OfficePage');
          return { Component: OfficePage };
        },
      },
      {
        path: 'ai-team/teams',
        lazy: async () => {
          const { TeamsPage } = await import('@/routes/ai-team/TeamsPage');
          return { Component: TeamsPage };
        },
      },
      // User-configurable AI teams: seat-level builder (role × count, per-seat
      // skills/tools/autonomy), reusable role-slot templates, and the task pool.
      {
        path: 'ai-team/builder',
        lazy: async () => {
          const { TeamBuilderPage } = await import('@/routes/ai-team/TeamBuilderPage');
          return { Component: TeamBuilderPage };
        },
      },
      {
        path: 'ai-team/templates',
        lazy: async () => {
          const { TemplateGallery } = await import('@/routes/ai-team/TemplateGallery');
          return { Component: TemplateGallery };
        },
      },
      {
        path: 'ai-team/pool',
        lazy: async () => {
          const { TaskPoolBoard } = await import('@/routes/ai-team/TaskPoolBoard');
          return { Component: TaskPoolBoard };
        },
      },
      {
        // Drag-and-drop agentic workflow studio (BPMN import + DMN decisions +
        // live run visualization) — see routes/process/WorkflowStudioPage.
        path: 'workflows/studio',
        lazy: async () => {
          const { WorkflowStudioPage } = await import('@/routes/process/WorkflowStudioPage');
          return { Component: WorkflowStudioPage };
        },
      },
      {
        path: 'ai-team/control',
        lazy: async () => {
          const { ControlConsolePage } = await import('@/routes/ai-team/ControlConsolePage');
          return { Component: ControlConsolePage };
        },
      },
      {
        path: 'ai-team/audit',
        lazy: async () => {
          const { AuditTrailPage } = await import('@/routes/ai-team/AuditTrailPage');
          return { Component: AuditTrailPage };
        },
      },
      {
        path: 'ai-team/deliverables',
        lazy: async () => {
          const { DeliverablesPage } = await import('@/routes/ai-team/DeliverablesPage');
          return { Component: DeliverablesPage };
        },
      },
      {
        path: 'ai-team/schedules',
        lazy: async () => {
          const { SchedulesPage } = await import('@/routes/ai-team/SchedulesPage');
          return { Component: SchedulesPage };
        },
      },
      // Content + calendar (wave 2)
      {
        path: 'content',
        lazy: async () => {
          const { ContentListPage } = await import('@/routes/content/ContentListPage');
          return { Component: ContentListPage };
        },
      },
      {
        path: 'content/new',
        lazy: async () => {
          const { ContentEditorPage } = await import('@/routes/content/ContentEditorPage');
          return { Component: ContentEditorPage };
        },
      },
      // Carousel slide editor + PNG/PDF export (content-service /carousels/*).
      {
        path: 'content/carousel',
        lazy: async () => {
          const { CarouselEditorPage } = await import('@/routes/content/CarouselEditorPage');
          return { Component: CarouselEditorPage };
        },
      },
      // Native rich-content studio: infographic / GIF+MP4 / post-pack generators.
      {
        path: 'content/studio',
        lazy: async () => {
          const { RichContentStudioPage } = await import('@/routes/content/RichContentStudioPage');
          return { Component: RichContentStudioPage };
        },
      },
      // Diagram studio: embedded draw.io editor; diagrams are saved to the DAM
      // as editable PNGs (PNG + embedded mxGraph XML) and stay re-editable.
      {
        path: 'content/diagram-studio',
        lazy: async () => {
          const { DiagramStudioPage } = await import('@/routes/content/DiagramStudioPage');
          return { Component: DiagramStudioPage };
        },
      },
      // Source Studio: NotebookLM-style grounded-sources workspace (sets, artifacts,
      // grounded chat, 2-voice audio overview). Backend /api/v1/source-studio.
      {
        path: 'content/source-studio',
        lazy: async () => {
          const { SourceStudioPage } = await import('@/routes/content/SourceStudioPage');
          return { Component: SourceStudioPage };
        },
      },
      // Studio unifié: one brief -> one-click auto-repurpose (POST /content/repurpose).
      {
        path: 'content/unified',
        lazy: async () => {
          const { UnifiedStudioPage } = await import('@/routes/content/UnifiedStudioPage');
          return { Component: UnifiedStudioPage };
        },
      },
      // Video/audio studio: transcription, repurposed captioned clips, TTS
      // voiceover, cloned-voice, avatar, reel (content-service /content/{video,
      // audio}/*). Plan-gated content_studio; honest 501 without a provider key.
      {
        path: 'content/video-studio',
        lazy: async () => {
          const { VideoStudioPage } = await import('@/routes/content/VideoStudioPage');
          return { Component: VideoStudioPage };
        },
      },
      // AI media studio: NEW content-service ops — generative inpaint (fill/erase
      // a brushed mask) + one-click localize (base caption → per-locale +
      // per-network variants). Plan-gated content_studio; honest 404 (route not
      // deployed) / 501 (no provider key) degrade inline, never a fake result.
      {
        path: 'content/ai-studio',
        lazy: async () => {
          const { AiMediaStudioPage } = await import('@/routes/content/AiMediaStudioPage');
          return { Component: AiMediaStudioPage };
        },
      },
      {
        // Idea feed (content-service /api/v1/ideas): capture-URL, ranked feed, CRUD.
        path: 'content/ideas',
        lazy: async () => {
          const { IdeasFeedPage } = await import('@/routes/content/IdeasFeedPage');
          return { Component: IdeasFeedPage };
        },
      },
      {
        // Deduped: the hook library is reached via /ai/hook-library (same backend,
        // /api/v1/ai/hooks). Keep the old deep link alive as a redirect.
        path: 'content/hooks',
        element: <Navigate to="/ai/hook-library" replace />,
      },
      {
        // Multi-stage approval chain + notifications config (content-service
        // settings/approval-chain + settings/approval-notifications). Static
        // segment — ranked ahead of content/:id so it is never swallowed as an id.
        path: 'content/approvals',
        lazy: async () => {
          const { ApprovalChainSettingsPage } = await import('@/routes/content/ApprovalChainSettingsPage');
          return { Component: ApprovalChainSettingsPage };
        },
      },
      {
        // R13 swipe-approval deck over the nightly « lot quotidien » review queue.
        // Static segment — ranked ahead of content/:id.
        path: 'content/deck',
        lazy: async () => {
          const { SwipeDeckPage } = await import('@/routes/content/SwipeDeckPage');
          return { Component: SwipeDeckPage };
        },
      },
      {
        path: 'content/:id',
        lazy: async () => {
          const { ContentEditorPage } = await import('@/routes/content/ContentEditorPage');
          return { Component: ContentEditorPage };
        },
      },
      {
        // Operator review view — chain status, per-stage decisions, comment
        // thread, guest-link manager (content-service approval-status/comments/
        // approval-invites).
        path: 'content/:id/review',
        lazy: async () => {
          const { ContentReviewPage } = await import('@/routes/content/ContentReviewPage');
          return { Component: ContentReviewPage };
        },
      },
      {
        path: 'recommandations',
        lazy: async () => {
          const { RecommendationsPage } = await import('@/routes/advisor/RecommendationsPage');
          return { Component: RecommendationsPage };
        },
      },
      // Epic 5b — conditional post-automation rules (OFF by default; auto mode
      // gated on a prior dry-run).
      {
        path: 'automations',
        lazy: async () => {
          const { AutomationsPage } = await import('@/routes/automations/AutomationsPage');
          return { Component: AutomationsPage };
        },
      },
      // Epic 5c — "Ce qui marche par plateforme": confirmed learned patterns,
      // opt-in + erasure, and transpose-a-winning-post.
      {
        path: 'learning',
        lazy: async () => {
          const { LearningPatternsPage } = await import('@/routes/learning/LearningPatternsPage');
          return { Component: LearningPatternsPage };
        },
      },
      // Marketing & Comms Strategy engine (EPIC 7) — brief -> AI strategy draft
      // (positioning/pillars/channels/audiences + SMART objectives), GTM plan
      // timeline, goals dashboard, and launch controls (per-channel autonomy,
      // all OFF by default + Loi-25 notice) with the measure->recommend->improve
      // feed. Flat routes; each page renders the shared StrategyStepNav.
      {
        path: 'strategy-mktg',
        lazy: async () => {
          const { StrategyBuilderPage } = await import('@/routes/strategy-mktg/StrategyBuilderPage');
          return { Component: StrategyBuilderPage };
        },
      },
      // Gap I2 founder loop: R1 product brief, R4 weekly review, R3 /today.
      {
        path: 'strategy-mktg/brief',
        lazy: async () => {
          const { ProductBriefPage } = await import('@/routes/strategy-mktg/ProductBriefPage');
          return { Component: ProductBriefPage };
        },
      },
      {
        path: 'strategy-mktg/:id/review',
        lazy: async () => {
          const { WeeklyReviewPage } = await import('@/routes/strategy-mktg/WeeklyReviewPage');
          return { Component: WeeklyReviewPage };
        },
      },
      {
        path: 'today',
        lazy: async () => {
          const { TodayPage } = await import('@/routes/today/TodayPage');
          return { Component: TodayPage };
        },
      },
      // Autopilot from a URL — wizard + cockpit (plan feature `autopilot`).
      {
        path: 'autopilot',
        lazy: async () => {
          const { AutopilotPage } = await import('@/routes/autopilot/AutopilotPage');
          return { Component: AutopilotPage };
        },
      },
      // I5 / R10 — Launch Kit (PH / Show HN drafts, manual directory tracker,
      // launch-day runbook, press criteria). Static segment, ranks above :id.
      {
        path: 'strategy-mktg/launch-kit',
        lazy: async () => {
          const { LaunchKitPage } = await import('@/routes/strategy-mktg/LaunchKitPage');
          return { Component: LaunchKitPage };
        },
      },
      {
        path: 'strategy-mktg/:id',
        lazy: async () => {
          const { StrategyBuilderPage } = await import('@/routes/strategy-mktg/StrategyBuilderPage');
          return { Component: StrategyBuilderPage };
        },
      },
      {
        path: 'strategy-mktg/:id/plan',
        lazy: async () => {
          const { GtmPlanPage } = await import('@/routes/strategy-mktg/GtmPlanPage');
          return { Component: GtmPlanPage };
        },
      },
      {
        path: 'strategy-mktg/:id/goals',
        lazy: async () => {
          const { GoalsDashboardPage } = await import('@/routes/strategy-mktg/GoalsDashboardPage');
          return { Component: GoalsDashboardPage };
        },
      },
      {
        path: 'strategy-mktg/:id/launch',
        lazy: async () => {
          const { LaunchControlsPage } = await import('@/routes/strategy-mktg/LaunchControlsPage');
          return { Component: LaunchControlsPage };
        },
      },
      {
        path: 'calendar',
        lazy: async () => {
          const { CalendarPage } = await import('@/routes/calendar/CalendarPage');
          return { Component: CalendarPage };
        },
      },
      // Epic 1 — Agentic content engine (plan dazzling-jingling-thimble.md).
      {
        // 4-step calendar generator (mode → inputs → template+review → dry-run preview).
        path: 'planning/generate',
        lazy: async () => {
          const { GenerateCalendarWizard } = await import('@/routes/planning/GenerateCalendarWizard');
          return { Component: GenerateCalendarWizard };
        },
      },
      {
        // LinkedIn template library performance leaderboard.
        path: 'planning/templates',
        lazy: async () => {
          const { TemplateLeaderboardPage } = await import('@/routes/planning/TemplateLeaderboard');
          return { Component: TemplateLeaderboardPage };
        },
      },
      {
        // Agentic review loop workshop (+ composer edit-capture when ?postId=).
        path: 'planning/review',
        lazy: async () => {
          const { ReviewWorkshopPage } = await import('@/routes/planning/ReviewWorkshopPage');
          return { Component: ReviewWorkshopPage };
        },
      },
      // Platforms + publishing (wave 3)
      {
        path: 'platforms',
        lazy: async () => {
          const { PlatformsPage } = await import('@/routes/platforms/PlatformsPage');
          return { Component: PlatformsPage };
        },
      },
      {
        path: 'publishing',
        lazy: async () => {
          const { PublishingLayout } = await import('@/routes/publishing/PublishingLayout');
          return { Component: PublishingLayout };
        },
        children: [
          {
            index: true,
            lazy: async () => {
              const { PublishingPage } = await import('@/routes/publishing/PublishingPage');
              return { Component: PublishingPage };
            },
          },
          {
            path: 'history',
            lazy: async () => {
              const { PublishHistoryPage } = await import('@/routes/publishing/PublishHistoryPage');
              return { Component: PublishHistoryPage };
            },
          },
        ],
      },
      {
        // Evergreen / recycle-queue + content categories + RSS auto-post
        // (self-contained page, its own tabs — kept out of the composer shell).
        path: 'publishing/queue',
        lazy: async () => {
          const { ContentQueuePage } = await import('@/routes/publishing/queue/ContentQueuePage');
          return { Component: ContentQueuePage };
        },
      },
      {
        // Bulk / CSV scheduling — self-contained (own PageHeader), sibling of the
        // composer so it doesn't inherit the Composer/Historique sub-nav.
        path: 'publishing/bulk',
        lazy: async () => {
          const { BulkSchedulePage } = await import('@/routes/publishing/bulk/BulkSchedulePage');
          return { Component: BulkSchedulePage };
        },
      },
      {
        // Bulk Media Studio — upload/add many media then schedule them all to
        // any platform as post/story/reel/carousel at once (POST /publish/
        // bulk-media). Self-contained (own PageHeader), sibling of the composer.
        path: 'publishing/studio',
        lazy: async () => {
          const { BulkMediaStudioPage } = await import('@/routes/publishing/studio/BulkMediaStudioPage');
          return { Component: BulkMediaStudioPage };
        },
      },
      {
        // AI staged post wizard (theme → title → description → detail), self-
        // contained (own PageHeader), sibling of the composer.
        path: 'publishing/wizard',
        lazy: async () => {
          const { AiPostWizard } = await import('@/routes/publishing/AiPostWizard');
          return { Component: AiPostWizard };
        },
      },
      {
        // Post-publication tracking dashboard — picker + per-job detail.
        path: 'publishing/tracking/:jobId?',
        lazy: async () => {
          const { TrackingDashboard } = await import('@/routes/publishing/TrackingDashboard');
          return { Component: TrackingDashboard };
        },
      },
      // R15 — referral / affiliate programs for the tenant's own product.
      {
        path: 'growth/programs',
        lazy: async () => {
          const { ProgramsPage } = await import('@/routes/growth/ProgramsPage');
          return { Component: ProgramsPage };
        },
      },
      {
        path: 'growth/programs/:programId',
        lazy: async () => {
          const { ProgramsPage } = await import('@/routes/growth/ProgramsPage');
          return { Component: ProgramsPage };
        },
      },
      // Analytics (wave 5)
      {
        path: 'analytics',
        lazy: async () => {
          const { AnalyticsLayout } = await import('@/routes/analytics/AnalyticsLayout');
          return { Component: AnalyticsLayout };
        },
        children: [
          { index: true, element: <Navigate to="/analytics/overview" replace /> },
          {
            path: 'overview',
            lazy: async () => {
              const { AnalyticsOverviewPage } = await import('@/routes/analytics/AnalyticsOverviewPage');
              return { Component: AnalyticsOverviewPage };
            },
          },
          {
            path: 'insights',
            lazy: async () => {
              const { AiInsightsPage } = await import('@/routes/analytics/AiInsightsPage');
              return { Component: AiInsightsPage };
            },
          },
          {
            path: 'hashtags',
            lazy: async () => {
              const { HashtagsPage } = await import('@/routes/analytics/HashtagsPage');
              return { Component: HashtagsPage };
            },
          },
          {
            // Person watchlists / person-metrics (Comptes suivis)
            path: 'people',
            lazy: async () => {
              const { PeopleWatchlistsPage } = await import('@/routes/analytics/PeopleWatchlistsPage');
              return { Component: PeopleWatchlistsPage };
            },
          },
          {
            path: 'competitors',
            lazy: async () => {
              const { CompetitorsPage } = await import('@/routes/analytics/CompetitorsPage');
              return { Component: CompetitorsPage };
            },
          },
          {
            path: 'influencers',
            lazy: async () => {
              const { InfluencersPage } = await import('@/routes/analytics/InfluencersPage');
              return { Component: InfluencersPage };
            },
          },
          {
            path: 'funnels',
            lazy: async () => {
              const { FunnelsPage } = await import('@/routes/analytics/FunnelsPage');
              return { Component: FunnelsPage };
            },
          },
          // R16 — in-app onboarding checklists (list + builder).
          {
            path: 'checklists',
            lazy: async () => {
              const { ChecklistsPage } = await import('@/routes/analytics/ChecklistsPage');
              return { Component: ChecklistsPage };
            },
          },
          {
            path: 'checklists/:checklistId',
            lazy: async () => {
              const { ChecklistsPage } = await import('@/routes/analytics/ChecklistsPage');
              return { Component: ChecklistsPage };
            },
          },
          {
            path: 'cohorts',
            lazy: async () => {
              const { CohortsPage } = await import('@/routes/analytics/CohortsPage');
              return { Component: CohortsPage };
            },
          },
          {
            path: 'attribution',
            lazy: async () => {
              const { AttributionPage } = await import('@/routes/analytics/AttributionPage');
              return { Component: AttributionPage };
            },
          },
          {
            path: 'goals',
            lazy: async () => {
              const { GoalsPage } = await import('@/routes/analytics/GoalsPage');
              return { Component: GoalsPage };
            },
          },
          {
            path: 'reports',
            lazy: async () => {
              const { ReportsPage } = await import('@/routes/analytics/ReportsPage');
              return { Component: ReportsPage };
            },
          },
          {
            path: 'monitoring',
            lazy: async () => {
              const { MonitoringPage } = await import('@/routes/analytics/monitoring/MonitoringPage');
              return { Component: MonitoringPage };
            },
          },
          {
            // Inbox / team performance analytics
            path: 'inbox-performance',
            lazy: async () => {
              const { InboxPerformancePage } = await import('@/routes/analytics/InboxPerformancePage');
              return { Component: InboxPerformancePage };
            },
          },
          {
            // I5 / R9 — verified-domain landing CRO + technical SEO + GEO audit.
            path: 'site-audit',
            lazy: async () => {
              const { SiteAuditPage } = await import('@/routes/analytics/SiteAuditPage');
              return { Component: SiteAuditPage };
            },
          },
          {
            // Team productivity — published posts + inbox responsiveness
            path: 'team-productivity',
            lazy: async () => {
              const { TeamProductivityPage } = await import('@/routes/analytics/TeamProductivityPage');
              return { Component: TeamProductivityPage };
            },
          },
          {
            // Audience demographics (age/gender, countries, cities, languages)
            path: 'audience',
            lazy: async () => {
              const { AudiencePage } = await import('@/routes/analytics/AudiencePage');
              return { Component: AudiencePage };
            },
          },
          {
            // Best-time 7×24 heatmap
            path: 'best-time',
            lazy: async () => {
              const { BestTimeHeatmapPage } = await import('@/routes/analytics/BestTimeHeatmapPage');
              return { Component: BestTimeHeatmapPage };
            },
          },
          {
            // First-party web traffic analytics (UTM medium/campaign breakdowns)
            path: 'web-traffic',
            lazy: async () => {
              const { WebTrafficPage } = await import('@/routes/analytics/WebTrafficPage');
              return { Component: WebTrafficPage };
            },
          },
          {
            path: 'share-of-voice',
            lazy: async () => {
              const { ShareOfVoicePage } = await import('@/routes/analytics/ShareOfVoicePage');
              return { Component: ShareOfVoicePage };
            },
          },
          // I8 — ASO audit (iTunes Lookup / pasted Android listing) + the PR
          // media-database external-blocker boundary.
          {
            path: 'aso',
            lazy: async () => {
              const { AsoAuditPage } = await import('@/routes/analytics/AsoAuditPage');
              return { Component: AsoAuditPage };
            },
          },
          {
            path: 'pr-media',
            lazy: async () => {
              const { PrMediaPage } = await import('@/routes/analytics/PrMediaPage');
              return { Component: PrMediaPage };
            },
          },
          {
            path: 'vision',
            lazy: async () => {
              const { VisionPage } = await import('@/routes/analytics/VisionPage');
              return { Component: VisionPage };
            },
          },
          {
            path: 'branding',
            lazy: async () => {
              const { BrandingPage } = await import('@/routes/analytics/BrandingPage');
              return { Component: BrandingPage };
            },
          },
          // Intelligence surfaces (analytics-intelligence-service, gateway
          // prefix /api/v1/intelligence) — 18 endpoints previously live but
          // UI-less: saved intelligence dashboards, sentiment, trends, CRM insights.
          {
            path: 'dashboards',
            lazy: async () => {
              const { IntelDashboardsPage } = await import('@/routes/analytics/IntelDashboardsPage');
              return { Component: IntelDashboardsPage };
            },
          },
          {
            path: 'sentiment',
            lazy: async () => {
              const { SentimentPage } = await import('@/routes/analytics/SentimentPage');
              return { Component: SentimentPage };
            },
          },
          {
            path: 'trends',
            lazy: async () => {
              const { TrendsPage } = await import('@/routes/analytics/TrendsPage');
              return { Component: TrendsPage };
            },
          },
          {
            path: 'crm-insights',
            lazy: async () => {
              const { CrmInsightsPage } = await import('@/routes/analytics/CrmInsightsPage');
              return { Component: CrmInsightsPage };
            },
          },
          {
            path: 'geo',
            lazy: async () => {
              const { GeoHubPage } = await import('@/routes/geo/GeoHubPage');
              return { Component: GeoHubPage };
            },
          },
          // R7 — first-party analytics SDK installer (ingest keys + live verification)
          {
            path: 'install',
            lazy: async () => {
              const { InstallPage } = await import('@/routes/analytics/InstallPage');
              return { Component: InstallPage };
            },
          },
          // R20 — experiments (A/B on pricing / landing / channel / onboarding)
          {
            path: 'experiments',
            lazy: async () => {
              const { ExperimentsPage } = await import('@/routes/analytics/ExperimentsPage');
              return { Component: ExperimentsPage };
            },
          },
        ],
      },
      // Graphic designer (fabric.js) + workflow builder (React Flow)
      {
        path: 'design',
        lazy: async () => {
          const { DesignGalleryPage } = await import('@/routes/design/DesignGalleryPage');
          return { Component: DesignGalleryPage };
        },
      },
      {
        path: 'design/editor/:id',
        lazy: async () => {
          const { DesignEditorPage } = await import('@/routes/design/DesignEditorPage');
          return { Component: DesignEditorPage };
        },
      },
      {
        path: 'design/brand-kits',
        lazy: async () => {
          const { BrandKitPage } = await import('@/routes/design/BrandKitPage');
          return { Component: BrandKitPage };
        },
      },
      {
        path: 'workflows',
        lazy: async () => {
          const { WorkflowsListPage } = await import('@/routes/workflows/WorkflowsListPage');
          return { Component: WorkflowsListPage };
        },
      },
      {
        path: 'workflows/:id',
        lazy: async () => {
          const { WorkflowBuilderPage } = await import('@/routes/workflows/WorkflowBuilderPage');
          return { Component: WorkflowBuilderPage };
        },
      },
      {
        path: 'media-library',
        lazy: async () => {
          const { MediaLibraryPage } = await import('@/routes/media-library/MediaLibraryPage');
          return { Component: MediaLibraryPage };
        },
      },
      // Documents hub — classify / organise received + issued invoices and
      // business documents (factures reçues/émises, demandes de service, offres,
      // devis, contrats) by month/vendor/category/type/status/source, with
      // drag-drop upload + single / selection / all-as-ZIP downloads
      // (doc-vault-service, gateway prefix /api/v1/doc-vault).
      {
        path: 'documents',
        lazy: async () => {
          const { DocumentsHubPage } = await import('@/routes/documents/DocumentsHubPage');
          return { Component: DocumentsHubPage };
        },
      },
      // AI personas / virtual team — activate + launch the ai-orchestrator agents.
      {
        path: 'ai-team',
        lazy: async () => {
          const { AITeamPage } = await import('@/routes/ai-team/AITeamPage');
          return { Component: AITeamPage };
        },
      },
      {
        path: 'ai-assistant',
        lazy: async () => {
          const { AiAssistantPage } = await import('@/routes/ai-assistant/AiAssistantPage');
          return { Component: AiAssistantPage };
        },
      },
      // Services marketplace (gap-fill) — provider directory, bookings & reviews.
      {
        path: 'marketplace',
        lazy: async () => {
          const { MarketplaceLayout } = await import('@/routes/marketplace/MarketplaceLayout');
          return { Component: MarketplaceLayout };
        },
        children: [
          { index: true, element: <Navigate to="/marketplace/browse" replace /> },
          {
            path: 'browse',
            lazy: async () => {
              const { BrowsePage } = await import('@/routes/marketplace/BrowsePage');
              return { Component: BrowsePage };
            },
          },
          {
            // Cross-tenant catalogue: PUBLISHED listings of every tenant.
            path: 'catalog',
            lazy: async () => {
              const { CatalogPage } = await import('@/routes/marketplace/CatalogPage');
              return { Component: CatalogPage };
            },
          },
          {
            // Platform-superadmin moderation of the cross-tenant catalogue.
            path: 'moderation',
            lazy: async () => {
              const { ModerationPage } = await import('@/routes/marketplace/ModerationPage');
              return { Component: ModerationPage };
            },
          },
          {
            // Provider detail as a Modal popup. The URL segment (/marketplace/
            // providers/:id) differs from the browse list, so it can't nest under
            // it; the modal opens over the marketplace shell, closing → browse.
            path: 'providers/:id',
            // Rendered as a RouteModal over its parent page: the page key ignores it
            // (DashboardLayout pageKeyOf) so the list under the modal stays mounted.
            handle: { routeModal: true },
            lazy: async () => {
              const { ProviderDetailPage } = await import('@/routes/marketplace/ProviderDetailPage');
              const { RouteModal } = await import('@/ui/RouteModal');
              return {
                Component: () => (
                  <RouteModal to="/marketplace/browse">
                    <ProviderDetailPage />
                  </RouteModal>
                ),
              };
            },
          },
          {
            path: 'dashboard',
            lazy: async () => {
              const { ProviderDashboardPage } = await import('@/routes/marketplace/ProviderDashboardPage');
              return { Component: ProviderDashboardPage };
            },
          },
          {
            path: 'my-bookings',
            lazy: async () => {
              const { MyBookingsPage } = await import('@/routes/marketplace/MyBookingsPage');
              return { Component: MyBookingsPage };
            },
          },
          {
            path: 'my-reviews',
            lazy: async () => {
              const { MyReviewsPage } = await import('@/routes/marketplace/MyReviewsPage');
              return { Component: MyReviewsPage };
            },
          },
        ],
      },
      // B2B prospection (gap-fill) — searchable directory, lists & enrichment.
      {
        path: 'prospect',
        lazy: async () => {
          const { ProspectLayout } = await import('@/routes/prospect/ProspectLayout');
          return { Component: ProspectLayout };
        },
        children: [
          { index: true, element: <Navigate to="/prospect/search" replace /> },
          {
            path: 'search',
            lazy: async () => {
              const { ProspectSearchPage } = await import('@/routes/prospect/ProspectSearchPage');
              return { Component: ProspectSearchPage };
            },
          },
          {
            path: 'lists',
            lazy: async () => {
              const { ProspectListsPage } = await import('@/routes/prospect/ProspectListsPage');
              return { Component: ProspectListsPage };
            },
          },
        ],
      },
      // Geo-targeted, multi-source commercial discovery (contract:
      // docs/PROSPECTION_CONTRACT.md). Distinct from /prospect (the B2B
      // directory search + build-a-list feature): this hits paid providers
      // (Google Places, OpenCorporates, Crunchbase, Apollo…) live per search.
      {
        path: 'prospection',
        lazy: async () => {
          const { DiscoveryPage } = await import('@/routes/prospection/DiscoveryPage');
          return { Component: DiscoveryPage };
        },
      },
      // Defined prospection STRATEGY — the AI plan that precedes the calendar
      // (/api/v1/prospection/strategy). Step 1 of the funnel; reachable from the
      // step-nav on every prospection page.
      {
        path: 'prospection/strategy',
        lazy: async () => {
          const { StrategyPage } = await import('@/routes/prospection/StrategyPage');
          return { Component: StrategyPage };
        },
      },
      // Offerings (own products/brands + ICP) and per-offering prospecting
      // (b2b-service /offerings, /offerings/{id}/prospect[/import]).
      {
        path: 'prospection/offres',
        lazy: async () => {
          const { OfferingsPage } = await import('@/routes/prospection/OfferingsPage');
          return { Component: OfferingsPage };
        },
      },
      // Rank prospects by goal/product fit (crm /scoring/recommend).
      {
        path: 'prospection/recommend',
        lazy: async () => {
          const { RecommendPage } = await import('@/routes/prospection/RecommendPage');
          return { Component: RecommendPage };
        },
      },
      {
        path: 'prospection/visitors',
        lazy: async () => {
          const { VisitorsPage } = await import('@/routes/prospection/VisitorsPage');
          return { Component: VisitorsPage };
        },
      },
      {
        path: 'prospection/job-change-alerts',
        lazy: async () => {
          const { JobChangeAlertsPage } = await import('@/routes/prospection/JobChangeAlertsPage');
          return { Component: JobChangeAlertsPage };
        },
      },
      {
        path: 'prospection/inbound-routers',
        lazy: async () => {
          const { InboundRoutersPage } = await import('@/routes/prospection/InboundRoutersPage');
          return { Component: InboundRoutersPage };
        },
      },
      // Dated multi-touch prospection calendar (/api/v1/prospection/calendar).
      {
        path: 'prospection/calendar',
        lazy: async () => {
          const { ProspectionCalendarPage } = await import('@/routes/prospection/ProspectionCalendarPage');
          return { Component: ProspectionCalendarPage };
        },
      },
      // Per-account AI intelligence dossier (/api/v1/prospection/account-intelligence).
      {
        path: 'prospection/account-intelligence',
        lazy: async () => {
          const { AccountIntelligencePage } = await import('@/routes/prospection/AccountIntelligencePage');
          return { Component: AccountIntelligencePage };
        },
      },
      {
        path: 'crm',
        lazy: async () => {
          const { CrmLayout } = await import('@/routes/crm/CrmLayout');
          return { Component: CrmLayout };
        },
        children: [
          { index: true, element: <Navigate to="/crm/contacts" replace /> },
          {
            path: 'contacts',
            lazy: async () => {
              const { ContactsPage } = await import('@/routes/crm/ContactsPage');
              return { Component: ContactsPage };
            },
          },
          {
            path: 'saved-searches',
            lazy: async () => {
              const { SavedSearchesPage } = await import('@/routes/crm/SavedSearchesPage');
              return { Component: SavedSearchesPage };
            },
          },
          {
            path: 'companies',
            lazy: async () => {
              const { CompaniesPage } = await import('@/routes/crm/CompaniesPage');
              return { Component: CompaniesPage };
            },
          },
          {
            path: 'groups',
            lazy: async () => {
              const { GroupsPage } = await import('@/routes/crm/GroupsPage');
              return { Component: GroupsPage };
            },
          },
          {
            path: 'pipeline',
            lazy: async () => {
              const { PipelinePage } = await import('@/routes/crm/PipelinePage');
              return { Component: PipelinePage };
            },
          },
          {
            path: 'leads',
            lazy: async () => {
              const { LeadsKanbanPage } = await import('@/routes/crm/LeadsKanbanPage');
              return { Component: LeadsKanbanPage };
            },
          },
          {
            path: 'prospecting',
            lazy: async () => {
              const { ProspectingGoalsPage } = await import('@/routes/crm/ProspectingGoalsPage');
              return { Component: ProspectingGoalsPage };
            },
          },
          {
            path: 'prospecting/campaigns/:id',
            lazy: async () => {
              const { ProspectingCampaignPage } = await import('@/routes/crm/ProspectingCampaignPage');
              return { Component: ProspectingCampaignPage };
            },
          },
          {
            path: 'settings',
            lazy: async () => {
              const { CrmSettingsPage } = await import('@/routes/crm/CrmSettingsPage');
              return { Component: CrmSettingsPage };
            },
          },
          {
            path: 'settings/assignment-rules/:id',
            lazy: async () => {
              const { AssignmentRuleDetailPage } = await import('@/routes/crm/AssignmentRuleDetailPage');
              return { Component: AssignmentRuleDetailPage };
            },
          },
          {
            path: 'follow-ups',
            lazy: async () => {
              const { FollowUpsPage } = await import('@/routes/crm/FollowUpsPage');
              return { Component: FollowUpsPage };
            },
          },
          {
            path: 'reminders',
            lazy: async () => {
              const { RemindersPage } = await import('@/routes/crm/RemindersPage');
              return { Component: RemindersPage };
            },
          },
          {
            path: 'duplicates',
            lazy: async () => {
              const { DuplicatesPage } = await import('@/routes/crm/DuplicatesPage');
              return { Component: DuplicatesPage };
            },
          },
          {
            path: 'shares',
            lazy: async () => {
              const { SharesPage } = await import('@/routes/crm/SharesPage');
              return { Component: SharesPage };
            },
          },
          {
            path: 'activity',
            lazy: async () => {
              const { ActivityFeedPage } = await import('@/routes/crm/ActivityFeedPage');
              return { Component: ActivityFeedPage };
            },
          },
          {
            path: 'dashboards',
            lazy: async () => {
              const { DashboardsPage } = await import('@/routes/crm/DashboardsPage');
              return { Component: DashboardsPage };
            },
          },
          {
            path: 'scheduler',
            lazy: async () => {
              const { SchedulerPage } = await import('@/routes/crm/SchedulerPage');
              return { Component: SchedulerPage };
            },
          },
          {
            path: 'forms',
            lazy: async () => {
              const { WebFormsPage } = await import('@/routes/crm/WebFormsPage');
              return { Component: WebFormsPage };
            },
          },
          {
            path: 'documents',
            lazy: async () => {
              const { DocumentsPage } = await import('@/routes/crm/DocumentsPage');
              return { Component: DocumentsPage };
            },
          },
          {
            path: 'signals',
            lazy: async () => {
              const { SignalsPage } = await import('@/routes/crm/signals/SignalsPage');
              return { Component: SignalsPage };
            },
          },
        ],
      },
      {
        path: 'social',
        lazy: async () => {
          const { SocialLeadsPage } = await import('@/routes/social/SocialLeadsPage');
          return { Component: SocialLeadsPage };
        },
      },
      {
        path: 'listening',
        lazy: async () => {
          const { ListeningFeedPage } = await import('@/routes/listening/ListeningFeedPage');
          return { Component: ListeningFeedPage };
        },
      },
      // Gap R5 — Community Conversation Finder (official HN/Bluesky/Dev.to/Reddit
      // APIs; drafts are copy-and-post-manually, no posting route exists).
      {
        path: 'listening/catches',
        lazy: async () => {
          const { CatchesPage } = await import('@/routes/listening/CatchesPage');
          return { Component: CatchesPage };
        },
      },
      {
        path: 'listening/monitors/:id',
        lazy: async () => {
          const { MonitorDetailPage } = await import('@/routes/community/MonitorDetailPage');
          return { Component: MonitorDetailPage };
        },
      },
      // Epic 6a — Veille / Listening dashboard (NET-NEW /api/v1/listening/*):
      // brand/keyword/competitor monitors, mentions feed with source honesty,
      // aggregated signals (volume/sentiment/topics) + alerts.
      {
        path: 'veille',
        lazy: async () => {
          const { ListeningDashboardPage } = await import('@/routes/listening/ListeningDashboardPage');
          return { Component: ListeningDashboardPage };
        },
      },
      // Epic 6d — white-label reports builder + competitor benchmark
      // (NET-NEW /api/v1/intelligence/insights/*).
      {
        path: 'reports',
        lazy: async () => {
          const { ReportsCenterPage } = await import('@/routes/reports/ReportsCenterPage');
          return { Component: ReportsCenterPage };
        },
      },
      // Epic 6g — influencer campaigns: shortlists, stage board, measurement
      // (NET-NEW /api/v1/b2b/influencers/shortlist + /b2b/influencer-campaigns).
      {
        path: 'influencers',
        lazy: async () => {
          const { InfluencerCampaignsPage } = await import('@/routes/influencers/InfluencerCampaignsPage');
          return { Component: InfluencerCampaignsPage };
        },
      },
      // Employee advocacy / amplify (EPIC 6f): feed, opt-in leaderboard, campaign admin.
      {
        path: 'advocacy',
        lazy: async () => {
          const { AdvocacyLayout } = await import('@/routes/advocacy/AdvocacyLayout');
          return { Component: AdvocacyLayout };
        },
        children: [
          { index: true, element: <Navigate to="/advocacy/feed" replace /> },
          {
            path: 'feed',
            lazy: async () => {
              const { AdvocateFeedPage } = await import('@/routes/advocacy/AdvocateFeedPage');
              return { Component: AdvocateFeedPage };
            },
          },
          {
            path: 'leaderboard',
            lazy: async () => {
              const { LeaderboardPage } = await import('@/routes/advocacy/LeaderboardPage');
              return { Component: LeaderboardPage };
            },
          },
          {
            path: 'campaigns',
            lazy: async () => {
              const { CampaignsAdminPage } = await import('@/routes/advocacy/CampaignsAdminPage');
              return { Component: CampaignsAdminPage };
            },
          },
        ],
      },
      // Content Communities — authentic advocacy amplification: a group's editor
      // publishes, members amplify from their OWN accounts with consent + (by
      // default) approval. Tabs: my engagements, group admin, policy, measurement.
      {
        path: 'communities',
        lazy: async () => {
          const { CommunitiesLayout } = await import('@/routes/communities/CommunitiesLayout');
          return { Component: CommunitiesLayout };
        },
        children: [
          { index: true, element: <Navigate to="/communities/my-engagements" replace /> },
          {
            path: 'my-engagements',
            lazy: async () => {
              const { MyEngagementsPage } = await import('@/routes/communities/MyEngagementsPage');
              return { Component: MyEngagementsPage };
            },
          },
          {
            path: 'groups',
            lazy: async () => {
              const { GroupsAdminPage } = await import('@/routes/communities/GroupsAdminPage');
              return { Component: GroupsAdminPage };
            },
          },
          {
            path: 'policy',
            lazy: async () => {
              const { PolicyPage } = await import('@/routes/communities/PolicyPage');
              return { Component: PolicyPage };
            },
          },
          {
            path: 'measurement',
            lazy: async () => {
              const { MeasurementPage } = await import('@/routes/communities/MeasurementPage');
              return { Component: MeasurementPage };
            },
          },
          // Gap R6 — account health & pacing levels (official read APIs + self-report).
          {
            path: 'health',
            lazy: async () => {
              const { AccountHealthPage } = await import('@/routes/communities/AccountHealthPage');
              return { Component: AccountHealthPage };
            },
          },
        ],
      },
      {
        path: 'bio',
        lazy: async () => {
          const { BioBuilderPage } = await import('@/routes/bio/BioBuilderPage');
          return { Component: BioBuilderPage };
        },
      },
      {
        path: 'ads',
        lazy: async () => {
          const { AdsPage } = await import('@/routes/ads/AdsPage');
          return { Component: AdsPage };
        },
      },
      {
        path: 'ads/ai-create',
        lazy: async () => {
          const { CreativeAdWizard } = await import('@/routes/ads/CreativeAdWizard');
          return { Component: CreativeAdWizard };
        },
      },
      {
        path: 'ads/campaigns/:campaignId/breakdowns',
        lazy: async () => {
          const { AdsBreakdownDashboard } = await import('@/routes/ads/AdsBreakdownDashboard');
          return { Component: AdsBreakdownDashboard };
        },
      },
      {
        // Epic 4 — full in-app ads command-center (per-ad engagement, creative
        // preview, billing, ads calendar, AI planner, update-published).
        path: 'ads/command',
        lazy: async () => {
          const { AdsCommandCenter } = await import('@/routes/ads/AdsCommandCenter');
          return { Component: AdsCommandCenter };
        },
      },
      {
        path: 'outreach',
        lazy: async () => {
          const { OutreachLayout } = await import('@/routes/outreach/OutreachLayout');
          return { Component: OutreachLayout };
        },
        children: [
          { index: true, element: <Navigate to="campaigns" replace /> },
          {
            path: 'campaigns',
            lazy: async () => {
              const { CampaignsPage } = await import('@/routes/outreach/CampaignsPage');
              return { Component: CampaignsPage };
            },
          },
          {
            path: 'sequences',
            lazy: async () => {
              const { SequencesPage } = await import('@/routes/outreach/SequencesPage');
              return { Component: SequencesPage };
            },
          },
          {
            // Multichannel task queue (LinkedIn/SMS/WhatsApp/call/manual steps)
            path: 'tasks',
            lazy: async () => {
              const { TasksPage } = await import('@/routes/outreach/TasksPage');
              return { Component: TasksPage };
            },
          },
          {
            // Click-to-call dialer + call log (email-service /api/v1/email/calls)
            path: 'dialer',
            lazy: async () => {
              const { DialerPage } = await import('@/routes/outreach/DialerPage');
              return { Component: DialerPage };
            },
          },
          {
            path: 'templates',
            lazy: async () => {
              const { TemplatesPage } = await import('@/routes/outreach/TemplatesPage');
              return { Component: TemplatesPage };
            },
          },
          {
            path: 'settings',
            lazy: async () => {
              const { OutreachSettingsPage } = await import('@/routes/outreach/OutreachSettingsPage');
              return { Component: OutreachSettingsPage };
            },
          },
          {
            path: 'unibox',
            lazy: async () => {
              const { UniboxPage } = await import('@/routes/outreach/UniboxPage');
              return { Component: UniboxPage };
            },
          },
          {
            path: 'sending',
            lazy: async () => {
              const { SendingPage } = await import('@/routes/outreach/SendingPage');
              return { Component: SendingPage };
            },
          },
        ],
      },
      {
        path: 'inbox',
        lazy: async () => {
          const { InboxPage } = await import('@/routes/inbox/InboxPage');
          return { Component: InboxPage };
        },
        children: [
          {
            index: true,
            lazy: async () => {
              const { InboxThreadPage } = await import('@/routes/inbox/InboxThreadPage');
              return { Component: InboxThreadPage };
            },
          },
          {
            path: 'approvals',
            lazy: async () => {
              const { ApprovalsQueuePage } = await import('@/routes/inbox/ApprovalsQueuePage');
              return { Component: ApprovalsQueuePage };
            },
          },
          {
            path: 'automation',
            lazy: async () => {
              const { AutomationPage } = await import('@/routes/inbox/AutomationPage');
              return { Component: AutomationPage };
            },
            children: [
              {
                path: 'rules/:id',
                // Rendered as a RouteModal over its parent page: the page key ignores it
                // (DashboardLayout pageKeyOf) so the list under the modal stays mounted.
                handle: { routeModal: true },
                lazy: async () => {
                  const { ModerationRuleDetailPage } = await import('@/routes/inbox/ModerationRuleDetailPage');
                  const { RouteModal } = await import('@/ui/RouteModal');
                  return {
                    Component: () => (
                      <RouteModal to="/inbox/automation">
                        <ModerationRuleDetailPage />
                      </RouteModal>
                    ),
                  };
                },
              },
              {
                path: 'schedules/:id',
                // Rendered as a RouteModal over its parent page: the page key ignores it
                // (DashboardLayout pageKeyOf) so the list under the modal stays mounted.
                handle: { routeModal: true },
                lazy: async () => {
                  const { AnimationScheduleDetailPage } = await import('@/routes/inbox/AnimationScheduleDetailPage');
                  const { RouteModal } = await import('@/ui/RouteModal');
                  return {
                    Component: () => (
                      <RouteModal to="/inbox/automation">
                        <AnimationScheduleDetailPage />
                      </RouteModal>
                    ),
                  };
                },
              },
              {
                path: 'reengagement/:id',
                // Rendered as a RouteModal over its parent page: the page key ignores it
                // (DashboardLayout pageKeyOf) so the list under the modal stays mounted.
                handle: { routeModal: true },
                lazy: async () => {
                  const { ReengagementPolicyDetailPage } = await import('@/routes/inbox/ReengagementPolicyDetailPage');
                  const { RouteModal } = await import('@/ui/RouteModal');
                  return {
                    Component: () => (
                      <RouteModal to="/inbox/automation">
                        <ReengagementPolicyDetailPage />
                      </RouteModal>
                    ),
                  };
                },
              },
            ],
          },
        ],
      },
      // Email Automation Suite — dedicated hub shell (PageHeader + tab strip +
      // Outlet) over the mailboxes / inbox / rules / templates pages. The visual
      // automation builder is a sibling full-screen route (like workflows/studio).
      {
        path: 'email',
        lazy: async () => {
          const { EmailHubPage } = await import('@/routes/email/EmailHubPage');
          return { Component: EmailHubPage };
        },
        children: [
          { index: true, element: <Navigate to="/email/mailboxes" replace /> },
          {
            path: 'mailboxes',
            lazy: async () => {
              const { MailboxesPage } = await import('@/routes/email/MailboxesPage');
              return { Component: MailboxesPage };
            },
          },
          {
            path: 'inbox',
            lazy: async () => {
              const { MailInboxPage } = await import('@/routes/email/MailInboxPage');
              return { Component: MailInboxPage };
            },
          },
          {
            path: 'rules',
            lazy: async () => {
              const { EmailRulesPage } = await import('@/routes/email/EmailRulesPage');
              return { Component: EmailRulesPage };
            },
          },
          {
            path: 'templates',
            lazy: async () => {
              const { EmailTemplatesPage } = await import('@/routes/email/EmailTemplatesPage');
              return { Component: EmailTemplatesPage };
            },
          },
          {
            // One-to-one manual composer (?draft_id= CRM draft, or ?to=&subject=).
            // Sends only after an explicit confirmation, through the email-service
            // send engine and its fail-closed CASL/Law-25 gate.
            path: 'compose',
            lazy: async () => {
              const { ComposePage } = await import('@/routes/email/ComposePage');
              return { Component: ComposePage };
            },
          },
        ],
      },
      {
        // Visual automation builder — full-screen React Flow canvas. The index
        // lists the tenant's automations; `:id` opens the drag-drop editor for one.
        path: 'email/builder',
        lazy: async () => {
          const { EmailAutomationBuilderPage } = await import('@/routes/email/EmailAutomationBuilderPage');
          return { Component: EmailAutomationBuilderPage };
        },
      },
      {
        path: 'email/builder/:id',
        lazy: async () => {
          const { EmailAutomationBuilderPage } = await import('@/routes/email/EmailAutomationBuilderPage');
          return { Component: EmailAutomationBuilderPage };
        },
      },
      {
        path: 'community',
        lazy: async () => {
          const { CommunityLayout } = await import('@/routes/community/CommunityLayout');
          return { Component: CommunityLayout };
        },
        children: [
          { index: true, element: <Navigate to="/community/dashboard" replace /> },
          // Epic 3 — AI Community Manager surfaces.
          {
            path: 'dashboard',
            lazy: async () => {
              const { CommentsDashboard } = await import('@/routes/community/CommentsDashboard');
              return { Component: CommentsDashboard };
            },
          },
          {
            path: 'automation',
            lazy: async () => {
              const { AutomationPolicyPage } = await import('@/routes/community/AutomationPolicyPage');
              return { Component: AutomationPolicyPage };
            },
          },
          {
            path: 'themes',
            lazy: async () => {
              const { ThemesPage } = await import('@/routes/community/ThemesPage');
              return { Component: ThemesPage };
            },
          },
          {
            path: 'targeting',
            lazy: async () => {
              const { TargetingEngagement } = await import('@/routes/community/TargetingEngagement');
              return { Component: TargetingEngagement };
            },
          },
          {
            path: 'canned',
            lazy: async () => {
              const { CannedMacrosPage } = await import('@/routes/community/CannedMacrosPage');
              return { Component: CannedMacrosPage };
            },
            children: [
              {
                path: 'responses/:id',
                // Rendered as a RouteModal over its parent page: the page key ignores it
                // (DashboardLayout pageKeyOf) so the list under the modal stays mounted.
                handle: { routeModal: true },
                lazy: async () => {
                  const { CannedResponseDetailPage } = await import('@/routes/community/CannedResponseDetailPage');
                  const { RouteModal } = await import('@/ui/RouteModal');
                  return {
                    Component: () => (
                      <RouteModal to="/community/canned">
                        <CannedResponseDetailPage />
                      </RouteModal>
                    ),
                  };
                },
              },
              {
                path: 'macros/:id',
                // Rendered as a RouteModal over its parent page: the page key ignores it
                // (DashboardLayout pageKeyOf) so the list under the modal stays mounted.
                handle: { routeModal: true },
                lazy: async () => {
                  const { MacroDetailPage } = await import('@/routes/community/MacroDetailPage');
                  const { RouteModal } = await import('@/ui/RouteModal');
                  return {
                    Component: () => (
                      <RouteModal to="/community/canned">
                        <MacroDetailPage />
                      </RouteModal>
                    ),
                  };
                },
              },
            ],
          },
          {
            path: 'editorial',
            lazy: async () => {
              const { EditorialProfilesPage } = await import(
                '@/routes/community/EditorialProfilesPage'
              );
              return { Component: EditorialProfilesPage };
            },
          },
          {
            path: 'engagement',
            lazy: async () => {
              const { EngagerRulesPage } = await import('@/routes/community/EngagerRulesPage');
              return { Component: EngagerRulesPage };
            },
          },
          {
            path: 'sla',
            lazy: async () => {
              const { SlaPoliciesPage } = await import('@/routes/community/SlaPoliciesPage');
              return { Component: SlaPoliciesPage };
            },
            children: [
              {
                path: ':id',
                // Rendered as a RouteModal over its parent page: the page key ignores it
                // (DashboardLayout pageKeyOf) so the list under the modal stays mounted.
                handle: { routeModal: true },
                lazy: async () => {
                  const { SlaPolicyDetailPage } = await import('@/routes/community/SlaPolicyDetailPage');
                  const { RouteModal } = await import('@/ui/RouteModal');
                  return {
                    Component: () => (
                      <RouteModal to="/community/sla">
                        <SlaPolicyDetailPage />
                      </RouteModal>
                    ),
                  };
                },
              },
            ],
          },
          {
            path: 'csat',
            lazy: async () => {
              const { CsatDashboardPage } = await import('@/routes/community/CsatDashboardPage');
              return { Component: CsatDashboardPage };
            },
          },
          {
            path: 'share-of-voice',
            lazy: async () => {
              const { CompetitiveVoicePage } = await import('@/routes/community/CompetitiveVoicePage');
              return { Component: CompetitiveVoicePage };
            },
          },
          {
            path: 'campaigns',
            lazy: async () => {
              const { InfluencerCampaignsPage } = await import('@/routes/community/InfluencerCampaignsPage');
              return { Component: InfluencerCampaignsPage };
            },
            // Detail nested so the campaigns list stays mounted and the detail
            // opens as a Modal popup on top of it (via <Outlet/> + RouteModal).
            children: [
              {
                path: ':id',
                // Rendered as a RouteModal over its parent page: the page key ignores it
                // (DashboardLayout pageKeyOf) so the list under the modal stays mounted.
                handle: { routeModal: true },
                lazy: async () => {
                  const { InfluencerCampaignDetailPage } = await import(
                    '@/routes/community/InfluencerCampaignDetailPage'
                  );
                  const { RouteModal } = await import('@/ui/RouteModal');
                  return {
                    Component: () => (
                      <RouteModal to="/community/campaigns">
                        <InfluencerCampaignDetailPage />
                      </RouteModal>
                    ),
                  };
                },
              },
            ],
          },
        ],
      },
      {
        path: 'projects',
        lazy: async () => {
          const { ProjectsListPage } = await import('@/routes/projects/ProjectsListPage');
          return { Component: ProjectsListPage };
        },
        // Detail nested → opens as a Modal over the live projects list.
        children: [
          {
            path: ':id',
            // Rendered as a RouteModal over its parent page: the page key ignores it
            // (DashboardLayout pageKeyOf) so the list under the modal stays mounted.
            handle: { routeModal: true },
            lazy: async () => {
              const { ProjectDetailPage } = await import('@/routes/projects/ProjectDetailPage');
              const { RouteModal } = await import('@/ui/RouteModal');
              return {
                Component: () => (
                  <RouteModal to="/projects">
                    <ProjectDetailPage />
                  </RouteModal>
                ),
              };
            },
          },
        ],
      },
      {
        path: 'projects/accept-invite/:token',
        lazy: async () => {
          const { AcceptInvitePage } = await import('@/routes/projects/AcceptInvitePage');
          return { Component: AcceptInvitePage };
        },
      },
      {
        path: 'approvals/*',
        lazy: async () => {
          const { ApprovalsInboxPage } = await import('@/routes/approvals/ApprovalsInboxPage');
          return { Component: ApprovalsInboxPage };
        },
      },
      {
        path: 'staff-questions/*',
        lazy: async () => {
          const { QuestionsInboxPage } = await import('@/routes/staff-questions/QuestionsInboxPage');
          return { Component: QuestionsInboxPage };
        },
      },
      {
        path: 'grants/*',
        lazy: async () => {
          const { GrantRequestsPage } = await import('@/routes/grants/GrantRequestsPage');
          return { Component: GrantRequestsPage };
        },
      },
      {
        // The legacy tenant-shell fleet page was superseded by the admin
        // console (`/admin/fleet`, same `lib/api/superadmin.ts` client, richer
        // tenants view). Keep the old deep link alive as a redirect only.
        path: 'superadmin',
        element: <Navigate to="/admin/fleet" replace />,
      },
      {
        path: 'ai-activity/*',
        lazy: async () => {
          const { AIActivityPage } = await import('@/routes/ai-activity/AIActivityPage');
          return { Component: AIActivityPage };
        },
      },
      // Team orchestration — goal -> multi-persona plan DAG -> live execution
      // (docs/PERSONA_ORCHESTRATION_CONTRACT.md §5/§7). Wires /api/v1/mas/*.
      {
        path: 'orchestration',
        lazy: async () => {
          const { OrchestrationPage } = await import('@/routes/orchestration/OrchestrationPage');
          return { Component: OrchestrationPage };
        },
      },
      // ---------------------------------------------------------------------
      // IA — AI Suite activation pages. Each surfaces a real ai-orchestrator
      // capability (see frontend-spa/src/lib/api/ai-suite.ts). /ai-team is the
      // discovery hub linking to every page below.
      // ---------------------------------------------------------------------
      {
        path: 'ai/content-studio',
        lazy: async () => {
          const { ContentStudioPage } = await import('@/routes/ai/content-studio/ContentStudioPage');
          return { Component: ContentStudioPage };
        },
      },
      {
        path: 'ai/longform',
        lazy: async () => {
          const { LongFormStudioPage } = await import('@/routes/ai/longform/LongFormStudioPage');
          return { Component: LongFormStudioPage };
        },
      },
      {
        path: 'ai/canvas',
        lazy: async () => {
          const { CanvasPage } = await import('@/routes/ai/canvas/CanvasPage');
          return { Component: CanvasPage };
        },
      },
      {
        path: 'ai/grid',
        lazy: async () => {
          const { GridStudioPage } = await import('@/routes/ai/grid/GridStudioPage');
          return { Component: GridStudioPage };
        },
      },
      {
        path: 'ai/repurpose',
        lazy: async () => {
          const { RepurposePage } = await import('@/routes/ai/repurpose/RepurposePage');
          return { Component: RepurposePage };
        },
      },
      {
        path: 'ai/hook-library',
        lazy: async () => {
          const { HookLibraryPage } = await import('@/routes/ai/hook-library/HookLibraryPage');
          return { Component: HookLibraryPage };
        },
      },
      {
        path: 'ai/brand-voice',
        lazy: async () => {
          const { BrandVoicePage } = await import('@/routes/ai/brand-voice/BrandVoicePage');
          return { Component: BrandVoicePage };
        },
      },
      {
        path: 'ai/ab-copy',
        lazy: async () => {
          const { AbCopyPage } = await import('@/routes/ai/ab-copy/AbCopyPage');
          return { Component: AbCopyPage };
        },
      },
      {
        path: 'ai/suggestions',
        lazy: async () => {
          const { SuggestionsPage } = await import('@/routes/ai/suggestions/SuggestionsPage');
          return { Component: SuggestionsPage };
        },
      },
      {
        path: 'ai/personalization',
        lazy: async () => {
          const { PersonalizationPage } = await import('@/routes/ai/personalization/PersonalizationPage');
          return { Component: PersonalizationPage };
        },
      },
      {
        path: 'ai/community-ai',
        lazy: async () => {
          const { CommunityAiPage } = await import('@/routes/ai/community-ai/CommunityAiPage');
          return { Component: CommunityAiPage };
        },
      },
      {
        path: 'ai/rag-documents',
        lazy: async () => {
          const { RagDocumentsPage } = await import('@/routes/ai/rag-documents/RagDocumentsPage');
          return { Component: RagDocumentsPage };
        },
      },
      {
        path: 'ai/memory',
        lazy: async () => {
          const { MemoryPage } = await import('@/routes/ai/memory/MemoryPage');
          return { Component: MemoryPage };
        },
      },
      {
        path: 'ai/ghostwriter',
        lazy: async () => {
          const { GhostwriterPage } = await import('@/routes/ai/ghostwriter/GhostwriterPage');
          return { Component: GhostwriterPage };
        },
      },
      {
        path: 'settings',
        lazy: async () => {
          const { SettingsLayout } = await import('@/routes/settings/SettingsLayout');
          return { Component: SettingsLayout };
        },
        children: [
          { index: true, element: <Navigate to="credits" replace /> },
          {
            path: 'credits',
            lazy: async () => {
              const { CreditsSettingsPage } = await import('@/routes/settings/CreditsSettingsPage');
              return { Component: CreditsSettingsPage };
            },
          },
          {
            // Org-admin: per-user AI-credit budgets (caps + usage).
            path: 'credits/budgets',
            lazy: async () => {
              const { UserBudgetsPage } = await import('@/features/credits/UserBudgetsPage');
              return { Component: UserBudgetsPage };
            },
          },
          {
            // Alias — "Facturation" surfaces the same credits + purchase page.
            path: 'billing',
            lazy: async () => {
              const { CreditsSettingsPage } = await import('@/routes/settings/CreditsSettingsPage');
              return { Component: CreditsSettingsPage };
            },
          },
          {
            path: 'subscription',
            lazy: async () => {
              const { SubscriptionPage } = await import('@/routes/settings/SubscriptionPage');
              return { Component: SubscriptionPage };
            },
          },
          {
            path: 'referral',
            lazy: async () => {
              const { ReferralPage } = await import('@/routes/settings/ReferralPage');
              return { Component: ReferralPage };
            },
          },
          {
            path: 'ai-registry',
            lazy: async () => {
              const { AIRegistryPage } = await import('@/routes/settings/AIRegistryPage');
              return { Component: AIRegistryPage };
            },
          },
          {
            // Admin model config — allow-in-picker, credit multiplier/cost,
            // and the Auto default-per-task mapping (GET /api/v1/models/config,
            // PATCH /api/v1/models/{id}/picker). Admin-gated inside the page.
            path: 'models',
            lazy: async () => {
              const { ModelsAdminPage } = await import('@/routes/settings/ModelsAdminPage');
              return { Component: ModelsAdminPage };
            },
          },
          {
            // BYO-LLM-key settings — a tenant's own provider API key(s)
            // (ai-orchestrator /api/v1/models/byok). Plan-gated on `byok_llm`.
            path: 'byok',
            lazy: async () => {
              const { ByokKeysPage } = await import('@/routes/settings/ByokKeysPage');
              return { Component: ByokKeysPage };
            },
          },
          {
            // SMTP sending mailboxes + domain purchasing (email-service
            // /api/v1/email/mailbox + /domain-purchase). Domain buy is plan-gated.
            path: 'mailboxes',
            lazy: async () => {
              const { MailboxesPage } = await import('@/routes/settings/MailboxesPage');
              return { Component: MailboxesPage };
            },
          },
          {
            path: 'governance',
            lazy: async () => {
              const { GovernancePage } = await import('@/routes/settings/GovernancePage');
              return { Component: GovernancePage };
            },
          },
          {
            // Epic 1 — content-engine automation policy (all OFF by default +
            // Loi-25 notice + "effacer mes données d'apprentissage").
            path: 'content-engine',
            lazy: async () => {
              const { ContentEnginePolicyPage } = await import(
                '@/routes/planning/ContentEnginePolicyPanel'
              );
              return { Component: ContentEnginePolicyPage };
            },
          },
          {
            // Review policy (Epic 2 2b) — auto-publish master switch + rules +
            // simulate. Default MANUAL/OFF, Loi-25 notice gated.
            path: 'review-policy',
            lazy: async () => {
              const { ReviewPolicy } = await import('@/routes/settings/ReviewPolicy');
              return { Component: ReviewPolicy };
            },
          },
          {
            // Law-25 / CASL Trust Center — consent ledger, DNC/unsubscribe,
            // regime config, data-subject erasure (compliance-service).
            path: 'trust-center',
            lazy: async () => {
              const { TrustCenterPage } = await import('@/routes/settings/TrustCenterPage');
              return { Component: TrustCenterPage };
            },
          },
          {
            // R18 — customer case studies with written consent (compliance-service).
            path: 'case-studies',
            lazy: async () => ({ Component: (await import('@/routes/settings/CaseStudiesSettingsPage')).CaseStudiesSettingsPage }),
          },
          {
            // Organization identity (name/domain/plan/quota) — tenant-service.
            path: 'organization',
            lazy: async () => {
              const { OrganizationSettingsPage } = await import('@/routes/settings/OrganizationSettingsPage');
              return { Component: OrganizationSettingsPage };
            },
          },
          {
            // Notification preferences surfaced in Settings. NotificationPrefs
            // renders as a Modal (needs onClose) → wrap to navigate back on close.
            path: 'notifications',
            lazy: async () => {
              const { NotificationPrefs } = await import('@/routes/notifications/NotificationPrefs');
              const { useNavigate } = await import('react-router');
              const NotificationsSettingsRoute = () => {
                const navigate = useNavigate();
                return <NotificationPrefs onClose={() => navigate('/settings/profile')} />;
              };
              return { Component: NotificationsSettingsRoute };
            },
          },
          {
            // Agency multi-brand workspaces admin (tenant-service + rbac-service).
            path: 'workspaces',
            lazy: async () => {
              const { WorkspacesPage } = await import('@/routes/settings/WorkspacesPage');
              return { Component: WorkspacesPage };
            },
          },
          {
            // Granular custom roles + resource permission matrix (rbac-service).
            path: 'roles',
            lazy: async () => {
              const { RolesPage } = await import('@/routes/settings/RolesPage');
              return { Component: RolesPage };
            },
          },
          {
            // Cross-account delegation — grant a person outside the org a
            // granular, per-platform right to manage posts/ads in this account.
            path: 'delegations',
            lazy: async () => {
              const { DelegationsPage } = await import('@/routes/settings/DelegationsPage');
              return { Component: DelegationsPage };
            },
          },
          {
            path: 'connectors',
            lazy: async () => {
              const { ConnectorConfigPage } = await import('@/routes/settings/ConnectorConfigPage');
              return { Component: ConnectorConfigPage };
            },
          },
          {
            // Public API / OAuth2 developer-app management
            path: 'developers',
            lazy: async () => {
              const { DeveloperApiPage } = await import('@/routes/settings/DeveloperApiPage');
              return { Component: DeveloperApiPage };
            },
          },
          {
            path: 'integrations',
            lazy: async () => {
              const { IntegrationsHubPage } = await import('@/routes/settings/IntegrationsHubPage');
              return { Component: IntegrationsHubPage };
            },
          },
          {
            // I8 — Copilot in Slack (/karexia) + SMS, and the in-app HIL queue.
            path: 'chat-apps',
            lazy: async () => {
              const { ChatAppsPage } = await import('@/routes/settings/ChatAppsPage');
              return { Component: ChatAppsPage };
            },
          },
          {
            path: 'profile',
            lazy: async () => {
              const { ProfileSettingsPage } = await import('@/routes/settings/ProfileSettingsPage');
              return { Component: ProfileSettingsPage };
            },
          },
          {
            path: 'connections',
            lazy: async () => {
              const { ConnectionsSettingsPage } = await import('@/routes/settings/ConnectionsSettingsPage');
              return { Component: ConnectionsSettingsPage };
            },
          },
          // Collaboration (docs/COLLABORATION_CONTRACT.md §1 + §5) — org
          // membership roster, invite/role/remove. Lives under the dedicated
          // routes/team/** feature folder (its role-gating hook is also
          // imported by the CRM share dialog — see routes/crm/shares.tsx).
          {
            path: 'team',
            lazy: async () => {
              const { TeamPage } = await import('@/routes/team/TeamPage');
              return { Component: TeamPage };
            },
          },
        ],
      },
      // "/dashboard" + "/tableau-de-bord" are natural URLs users type (and the
      // marketing site shows "app.karexia.com/tableau-de-bord"), but the real
      // in-app home is the index route "/". Redirect them instead of 404'ing.
      // I8 — in-app human support conversations (email deep link ?thread=).
      {
        path: 'support',
        lazy: async () => {
          const { SupportPage } = await import('@/routes/support/SupportPage');
          return { Component: SupportPage };
        },
      },
      { path: 'dashboard', element: <Navigate to="/" replace /> },
      { path: 'tableau-de-bord', element: <Navigate to="/" replace /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);
